import React, {useEffect, useRef, useState} from 'react';
import {fetchFromHub} from '../../actions/networkActions';
import {useDispatch, useSelector} from 'react-redux';
import * as L from 'leaflet';
import {Map, Marker, Popup, TileLayer, ZoomControl} from 'react-leaflet';
import {imagePath} from '../../utils/imageUtils';
import TrackerWeather from "./TrackerWeather";
import styled, {useTheme} from "styled-components";
import Checkbox from "../../components/Checkbox";
import {PropTypes} from "prop-types";
import {DefaultSelect, device} from "../../resources/styles";

const TrackerSettingsContainer = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    align-items: center;
    @media ${device.laptop} {
        grid-template-columns: 1fr 1fr 1fr 1fr;
    }
    span {
        color: ${props => props.theme.bodyColor || "#0a0a0a"};
    }
    >div {
        margin-bottom: 0.5rem;
    }
`

const StyledMapContainer = styled.div`
    @media (min-height: 640px) {
        .leaflet-container {
            height: 67vh;
        }
    }
    
    .leaflet-control-zoom-in, .leaflet-control-zoom-in:hover, .leaflet-control-zoom-in:focus {
        background-color: ${props => props.theme.buttonBackground || "#1e2c5c"};
        font-size: 1pt;
    }

    .leaflet-control-zoom-out, .leaflet-control-zoom-out:hover, .leaflet-control-zoom-out:focus {
        font-size: 1pt;
        background-color: ${props => props.theme.buttonBackground || "#1e2c5c"};
    }

    .leaflet-control-zoom-in:before {
        content: "\\f00e";
        font-family: FontAwesome;
        left: 5px;
        position: absolute;
        top: 0;
        font-weight: 100;
        font-size: 16pt;
        color: ${props => props.theme.buttonColor || "#ffffff"};
    }

    .leaflet-control-zoom-out:after {
        content: "\\f010";
        font-family: FontAwesome;
        left: 5px;
        position: absolute;
        top: 1.4em;
        font-weight: 100;
        font-size: 16pt;
        color: ${props => props.theme.buttonColor || "#ffffff"};
    }

    .leaflet-control-zoom {
        border: none;
        box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16), 0 3px 6px rgba(0, 0, 0, 0.23);
    }
`

const StyledPopup = styled(Popup)`
    display: ${props => props.hidden ? "none" : "block"};
    .leaflet-popup-content-wrapper {
        border-radius: 0;
        background: rgba(255,255,255,0.8);
        box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
    }
    .leaflet-popup-tip {
        background: rgba(255,255,255,0.8);
        box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
    }
    .leaflet-popup-tip-container {
        margin-top: 0;
        display: ${props => props.unknownDirection ? "none" : "block"};
    }
    .leaflet-popup-content {
        margin: 0.5rem;
        text-align: center;
    }
    z-index: ${props => props.isSelected ? 100 : 1};
`

const Tracker = (_props, context) => {
    const [gpsProfile, setGpsProfile] = useState(null);
    const [selectedShipGpsProfile, setSelectedShipGpsProfile] = useState(null);
    const [weather, setWeather] = useState(null);
    const [zoom, setZoom] = useState(4);
    const [autoCenterMap, setAutoCenterMap] = useState(true);
    const [customMapPosition, setCustomMapPosition] = useState(false);
    const [hideWeather, setHideWeather] = useState(false);
    const [hideNames, setHideNames] = useState(false);
    const [hideFleet, setHideFleet] = useState(false);
    const eventSourceRef = useRef(null);
    const markerRefs = useRef({});
    const configuration = useSelector(state => state.configuration);
    const dispatch = useDispatch();
    const theme = useTheme();

    // Display popup as each marker renders
    const handleMarkerRef = (name, ref) => {
        markerRefs.current[name] = ref ? ref.leafletElement : null;
        if (markerRefs.current[name] && !markerRefs.current[name].isPopupOpen()) {
            markerRefs.current[name].openPopup();
        }
    };

    useEffect(() => {
        const handleGpsData = (gpsData) => {
            let shipGpsProfiles = [];
            let currentShip = null;
            if (!gpsData || !Array.isArray(gpsData)) {
                return;
            }
            gpsData.forEach((ship) => {
                shipGpsProfiles = [...shipGpsProfiles, ship]
                if (ship.current) {
                    currentShip = ship;
                    setWeather(ship.advancedInformation?.weather)
                }
            })
            //Fallback in case data does not specify real current ship
            setSelectedShipGpsProfile(currentShip ? currentShip : gpsData[0]);
            setGpsProfile(shipGpsProfiles);
        }
        dispatch( fetchFromHub('gps/all', (gpsData) => handleGpsData(gpsData)) );
        eventSourceRef.current = new EventSource(configuration.hubApiUrl + 'cms/events/register');
        eventSourceRef.current.onmessage = (message) => {
            const data = JSON.parse(message.data);
            if (data.gpsProfile) {
                handleGpsData(data.gpsProfile);
            }
        };
        return () => {
            if (eventSourceRef.current) {
                eventSourceRef.current.close();
            }
        };
    }, [configuration, dispatch]);

    const isDirectionUnknown = (direction) => {
        return direction === undefined || direction === 511;
    }

    const calculateIconName = (dataType, heading, course) => {
        if (dataType !== 'Ship' && dataType !== 'Aircraft') {
            return 'circle.svg';
        }

        if (isDirectionUnknown(heading) && isDirectionUnknown(course)) {
            if (theme.shipLocationIconSrc) {
                return theme.shipLocationIconSrc
            }
            return "ship_location.png"
        }

        return dataType.toLowerCase() + '.png';
    };

    const calculateRotation = (heading, course) => {
        if (!isDirectionUnknown(heading)) {
            return `${heading - 90}deg`;
        }

        if (!isDirectionUnknown(course)) {
            return `${course - 90}deg`;
        }

        return "0deg";
    };

    const getIcon = (dataType, heading, course) => {

        return (
            L.divIcon({
                iconSize: [80, 80],
                className: 'someTestClassName',
                html: `<img 
                    alt="map marker"
                    style="transform: rotate(${calculateRotation(heading, course)}); margin-top:${isDirectionUnknown(heading, course) ? "-40px" : "0px"}"
                    height="80px"
                    width="80px"
                    src=${imagePath('map/' + calculateIconName(dataType, heading, course))}>`
            })
        )
    }

    const updateCustomPosition = (event) => {
        const mapCenter = event.target.getCenter();
        const mapPosition = [mapCenter.lat, mapCenter.lng];

        if(!autoCenterMap) {
            setCustomMapPosition(mapPosition)
        }
    };

    const toggleAutoCenter = () => {
        if(autoCenterMap) {
            setAutoCenterMap(false);
            setCustomMapPosition([selectedShipGpsProfile?.latitude, selectedShipGpsProfile?.longitude])
        }
        else {
            setAutoCenterMap(true);
            setCustomMapPosition(null);
        }
    };

    const settings = [
        {
            label: context.t("disableautocentering"),
            display: true,
            checked: !autoCenterMap,
            handleCheckboxChange: () => toggleAutoCenter()
        },
        {
            label: context.t("hideweather"),
            display: weather,
            checked: hideWeather,
            handleCheckboxChange: () => setHideWeather(prev => !prev)
        },
        {
            label: context.t("hide_ship_names"),
            display: gpsProfile && gpsProfile.length > 1,
            checked: hideNames,
            handleCheckboxChange: () => setHideNames(prev => !prev)
        },
        {
            label: context.t("hide_fleet"),
            display: gpsProfile && gpsProfile.length > 1,
            checked: hideFleet,
            handleCheckboxChange: () => setHideFleet(prev => !prev)
        },
    ]

    const mapPosition = autoCenterMap ? [selectedShipGpsProfile?.latitude, selectedShipGpsProfile?.longitude] : customMapPosition;

    if ( !gpsProfile || !selectedShipGpsProfile || !mapPosition ) {
        return (
            <p>{context.t('no_gps_data')}</p>
        )
    }

    return (
        <div>
            <h1>{context.t('tracker')}</h1>
            <>
                { gpsProfile && gpsProfile.length > 1 &&
                    <>
                        <p className="mb-0">{context.t('selected_ship')}:</p>
                        <DefaultSelect
                            defaultValue={selectedShipGpsProfile.id}
                            onChange={(e) => setSelectedShipGpsProfile(gpsProfile.find((ship) => ship.id === e.target.value))}
                        >
                            { gpsProfile.map((ship) =>
                                <option key={ship.id} value={ship.id}>
                                    {ship.profileName} {ship.current && "*"}
                                </option>
                            ) }
                        </DefaultSelect>
                    </>
                }
                <TrackerSettingsContainer>
                    { settings.filter((setting) => setting.display)
                        .map((setting, index) =>
                            <Checkbox
                                key={index}
                                label={setting.label}
                                checked={setting.checked}
                                handleCheckboxChange={setting.handleCheckboxChange}
                            />
                        )}
                </TrackerSettingsContainer>
            </>
            <div className="relative">
                <StyledMapContainer>
                    <Map
                        zoomControl={false}
                        center={mapPosition}
                        zoom={zoom}
                        minZoom={2}
                        maxZoom={14}
                        onZoomEnd={(event) => setZoom(event.target.getZoom())}
                        onMoveEnd={updateCustomPosition}
                        fadeAnimation={true}
                        attributionControl={false}
                        style={{minHeight: "300px", width: "100%"}}
                        closePopupOnClick={false}
                        doubleClickZoom={false}
                    >
                        <TileLayer url={configuration.hubSettings.osmServiceUrl}/>
                        <ZoomControl position='topright'/>
                        { gpsProfile?.map((ship) => !ship.current && hideFleet ?
                                null
                                :
                                <Marker
                                    key={ship.externalId}
                                    icon={getIcon(ship.dataType, ship.advancedInformation.heading, ship.advancedInformation.course)}
                                    position={[ship.latitude, ship.longitude]}
                                    ref={(ref) => handleMarkerRef(ship.profileName, ref)}
                                >
                                    { gpsProfile.length > 1 &&
                                        <StyledPopup
                                            offset={isDirectionUnknown(ship.advancedInformation.heading, ship.advancedInformation.course) ? [0,60] : [0,-25]}
                                            closeButton={false}
                                            autoClose={false}
                                            autoPan={false}
                                            hidden={hideNames}
                                            isSelected={ship.externalId === selectedShipGpsProfile.externalId}
                                            unknownDirection={isDirectionUnknown(ship.advancedInformation.heading, ship.advancedInformation.course)}
                                        >
                                            <div>{ship.profileName}</div>
                                        </StyledPopup>
                                    }
                                </Marker>
                        ) }
                    </Map>
                </StyledMapContainer>
                { ( weather && !hideWeather ) &&
                    <TrackerWeather weatherData={weather}/>
                }
            </div>
        </div>
    )
}

Tracker.contextTypes = {
    t: PropTypes.func.isRequired
}

export default Tracker;